import type { ReactNode } from 'react'
import type { CrmData } from '@/constants/crm'
import type { CrmState, CrmStore } from '@/stores/crm-store'

import { createContext, useContext, useEffect, useState } from 'react'
import { useStore } from 'zustand'
import { createCrmStore } from '@/stores/crm-store'

const CrmStoreContext = createContext<CrmStore | null>(null)

interface CrmStoreProviderProps {
    data?: CrmData | null
    today: string
    children: ReactNode
}

type CrmResponse = {
    data?: CrmData
}

function isCrmData(value: unknown): value is CrmData {
    if (!value || typeof value !== 'object') return false

    const data = value as Record<string, unknown>

    return ['companies', 'deals', 'contacts', 'activities', 'sequences'].every(key => Array.isArray(data[key]))
}

export function CrmStoreProvider({ data, today, children }: CrmStoreProviderProps) {
    const [store] = useState(() => createCrmStore({
        data: isCrmData(data) ? data : null,
        today: today || new Date().toISOString().slice(0, 10)
    }))

    useEffect(() => {
        if (store.getState().status !== 'loading') return

        const controller = new AbortController()

        async function load() {
            try {
                const response = await fetch('/api/crm', { signal: controller.signal })
                const payload = await response.json().catch(() => null) as CrmResponse | null

                if (!response.ok || !isCrmData(payload?.data)) throw new Error('The CRM data could not be loaded.')

                store.getState().setData(payload.data)
            }
            catch (error) {
                if (controller.signal.aborted) return

                console.error('Loading CRM data failed:', error instanceof Error ? error.message : error)
                store.getState().setStatus('error')
            }
        }

        load()

        return () => controller.abort()
    }, [store])

    return (
        <CrmStoreContext.Provider value={store}>
            {children}
        </CrmStoreContext.Provider>
    )
}

export function useCrmStoreApi(): CrmStore {
    const store = useContext(CrmStoreContext)

    if (!store) throw new Error('useCrmStore must be used inside CrmStoreProvider.')

    return store
}

export function useCrmStore<T>(selector: (state: CrmState) => T): T {
    return useStore(useCrmStoreApi(), selector)
}
